import React from "react";
import { Box, Link, Paper, Stack, Table, TableBody, TableContainer, TableHead, TableRow, TableSortLabel, Typography } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { ArrowDropDown, ArrowDropUp, Check, Close, Download, Info } from "@mui/icons-material";
import { checkUserDetailsFromCookie, fetchShareableCampaigns, manageShareableCampaignPaginationCookie } from "../../../api_calls/userManagement";
import NumberedModalPagination from "../../../components/commonComponents/LoadMore/NumberedModalPagination";
import { SpaceBetweenStack, StyledCheckbox, StyledDarkRedInformationTypography } from "../../../components/commonComponents/StyledComponents/styled";
import { ExtraPaddingLeftStyledCell, LeftStyledTableCell, StyledTableCell } from "../../../components/commonComponents/TabularLists/styles";
import { formatDate, SortIcon } from "../../../api_calls/utils";
import { updateStateData } from "../../../Store/actions";
import { CustomTextInput } from "../../../components/commonComponents/TextInputs/style";

import ListingDataTableForModal from "../../../components/commonComponents/TabularLists/ListingDataTableForModal";
import {
    ListingCellLeft,
    ListingCellCenter,
    ListingHeaderContent,
} from "../../../components/commonComponents/TabularLists/styles";

import { useListingTableControllerModal } from "../../../hooks/useListingTableControllerModal";


const SharedCampaignList = ({ ownerId, companyObj, fieldError, contentRef }) => {
    const sharedCampaigns = useSelector((state) => state.userSelections);
    const dispatchAction = useDispatch();
    const campaignList = sharedCampaigns.shareable_campaign_list || [];
    const [searchVal, setSearchVal] = React.useState({ value: "" });
    const checkUserDetails = checkUserDetailsFromCookie();

    let userDetailObj = {};
    if (checkUserDetails.success === true) {
        userDetailObj = checkUserDetails.user_detail;
    }

    // can select/unselect campaigns
    // (ownerId !== "" && ownerId !== userDetailObj.assigned_company.id && companyObj.label !== "Cyreen GmbH") || (companyObj.label === "Cyreen GmbH" && userDetailObj.user_type !== "cyreen")

    const canChangeSelectedCampaigns = ((companyObj.label.toLowerCase() === "cyreen gmbh" && userDetailObj.user_type === "cyreen") || (userDetailObj.assigned_company.id === ownerId && userDetailObj.user_type !== "cyreen"));

    const [sortingMeasure, setSortingMeasure] = React.useState({
        campaignName: "asc",
        endDate: "asc",
        brandCompany: "asc",
        brandName: "asc",
    });

    const handleSortingParamChange = (sortField, sortOrder) => {
        setSortingMeasure((prev) => ({
            ...prev,
            [sortField]: sortOrder === "asc" ? "desc" : "asc",
        }));

        dispatchAction(
            updateStateData({
                brand_campaign_sorting_measure: {
                    sortField,
                    sortOrder: sortOrder === "asc" ? "desc" : "asc",
                },
            })
        );

        const pageSize = pagination?.pageSize ?? 10;
        loadShareableCampaigns(0, pageSize);
    };


    // select/unselect campaigns
    const selectCampaigns = (campaignId, selectedFlag) => {
        const existingSelections = typeof sharedCampaigns.brand_campaign_selection !== "undefined" ? sharedCampaigns.brand_campaign_selection : [];

        if (selectedFlag === true) {
            dispatchAction(updateStateData({
                brand_campaign_selection: [...existingSelections, campaignId],
                user_interacted: true,
            }));
        } else {
            dispatchAction(updateStateData({
                brand_campaign_selection: existingSelections.filter(selection => selection !== campaignId),
                user_interacted: true,
            }));
        }
    }

    const loadShareableCampaigns = React.useCallback(
        (pageNumber, pageSize) => {
            // if (!ownerId) return;
            return fetchShareableCampaigns(ownerId, pageNumber, pageSize, searchVal.value);
        },
        [ownerId, searchVal.value]
    );

    const { pagination, onPageChange, onPageSizeChange, refresh } =
        useListingTableControllerModal({
            paginationReduxKey: "shareable_campaign_pagination",
            loadPage: loadShareableCampaigns,
            defaultPageSize: 10,
            scrollContainerRef: contentRef,
        });

    React.useEffect(() => {
        const pageSize = pagination?.pageSize ?? 10;
        loadShareableCampaigns(0, pageSize);
    }, [ownerId]);


    React.useEffect(() => {
        const handler = setTimeout(() => {
            // When searching, always go back to page 0 using current pageSize
            const pageSize = pagination?.pageSize ?? 10;
            loadShareableCampaigns(0, pageSize);
        }, 1000);

        return () => clearTimeout(handler);
    }, [searchVal.value, pagination?.pageSize, loadShareableCampaigns]);

    const columns = React.useMemo(
        () => [
            {
                id: "select",
                header: "Select",
                headerAlign: "center",
                align: "center",
                render: (campaignObj) => (
                    <ListingCellCenter>
                        <StyledCheckbox
                            size="small"
                            disabled={ownerId !== "" ? !canChangeSelectedCampaigns : false}
                            checked={
                                typeof sharedCampaigns.brand_campaign_selection !== "undefined"
                                    ? sharedCampaigns.brand_campaign_selection.includes(campaignObj.id)
                                    : false
                            }
                            onChange={(e) => selectCampaigns(campaignObj.id, e.target.checked)}
                        />
                    </ListingCellCenter>
                ),
            },

            {
                id: "name",
                header: (
                    <ListingHeaderContent onClick={() => handleSortingParamChange("campaignName", sortingMeasure.campaignName)} style={{ cursor: "pointer" }}>
                        Campaign Name
                        <SortIcon direction={sortingMeasure.campaignName} />
                    </ListingHeaderContent>
                ),
                cellClassName: "text-wrapper-store_list wrapLargeText",
                render: (c) => <ListingCellLeft>{c.name}</ListingCellLeft>,
            },

            {
                id: "endDate",
                header: (
                    <ListingHeaderContent onClick={() => handleSortingParamChange("endDate", sortingMeasure.endDate)} style={{ cursor: "pointer" }}>
                        End Date
                        <SortIcon direction={sortingMeasure.endDate} />
                    </ListingHeaderContent>
                ),
                cellClassName: "text-wrapper-store_list",
                render: (c) => <ListingCellLeft>{formatDate(c.date_end)}</ListingCellLeft>,
            },

            {
                id: "brandCompany",
                header: (
                    <ListingHeaderContent onClick={() => handleSortingParamChange("brandCompany", sortingMeasure.brandCompany)} style={{ cursor: "pointer" }}>
                        Brand Company
                        <SortIcon direction={sortingMeasure.brandCompany} />
                    </ListingHeaderContent>
                ),
                cellClassName: "text-wrapper-store_list",
                render: (c) => <ListingCellLeft>{c.company_brand?.name}</ListingCellLeft>,
            },

            {
                id: "brandName",
                header: (
                    <ListingHeaderContent onClick={() => handleSortingParamChange("brandName", sortingMeasure.brandName)} style={{ cursor: "pointer" }}>
                        Brand Name
                        <SortIcon direction={sortingMeasure.brandName} />
                    </ListingHeaderContent>
                ),
                cellClassName: "text-wrapper-store_list",
                render: (c) => <ListingCellLeft>{c.brand}</ListingCellLeft>,
            },
        ],
        [
            ownerId,
            canChangeSelectedCampaigns,
            sharedCampaigns.brand_campaign_selection,
            sortingMeasure,
        ]
    );

    return (
        <>
            {/* ---------------- filters ------------ */}
            {
                typeof sharedCampaigns.shareable_campaign_list !== "undefined" && (
                    <Box width={350}>
                        <CustomTextInput
                            fullWidth
                            variant="standard"
                            placeholder="Search"
                            label="Search"
                            autoComplete="off"
                            type={"text"}
                            value={searchVal.value}
                            onChange={(e) => {
                                setSearchVal({ value: e.target.value });
                            }}
                            onBlur={(e) => { }}
                        />
                    </Box>
                )
            }
            <Box height={15} />
            {/* ------------ stores availability listing ---------- */}
            {
                typeof sharedCampaigns.shareable_campaign_list !== "undefined" && (
                    <ListingDataTableForModal
                        columns={columns}
                        rows={campaignList}
                        rowKey={(row) => row.id}
                        emptyText="No results"
                    />
                )
            }
            {/* ----------- selected campaigns count ------------- */}
            {
                typeof sharedCampaigns.shareable_campaign_pagination !== "undefined" && (
                    <>
                    <Box height={10} />
                        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 2 }}>
                            <StyledDarkRedInformationTypography variant="body2">
                                Selected {typeof sharedCampaigns.brand_campaign_selection !== "undefined" ? sharedCampaigns.brand_campaign_selection.length : 0} campaign(s)
                                {/* of {sharedCampaigns.shareable_campaign_pagination.totalRecords}  */}
                            </StyledDarkRedInformationTypography>
                        </Stack>
                    </>
                )
            }
            <Box height={10} />
            {/* --------- record count info --------- */}
            {
                typeof sharedCampaigns.shareable_campaign_pagination !== "undefined" && (
                    <NumberedModalPagination
                        totalCount={sharedCampaigns.shareable_campaign_pagination.totalPages}
                        totalRecordCount={sharedCampaigns.shareable_campaign_pagination.totalRecords}
                        defaultSelectedPage={sharedCampaigns.shareable_campaign_pagination.pageNumber + 1}
                        changeCall={onPageChange}
                        rowSizeChangeCall={onPageSizeChange}
                        rowSize={sharedCampaigns.shareable_campaign_pagination.pageSize}
                    />

                )
            }
        </>
    );
};

export default SharedCampaignList;
